import {useContext, useEffect, useRef} from 'react';
import {Platform} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import moment from 'moment';
import {StoreContext} from '../components/StoreContextProvider';
import {LANDING_PAGE_URL, SESSION_EXPIRE_TIME} from '../constants';

type Options = {
  // Defaults to true. Most callers pass the screen's useIsFocused() value
  // so the timer only runs while the screen is in front. One screen
  // (User/Account) historically ran it unconditionally — omit to preserve
  // that.
  enabled?: boolean;
  // Runs inside Context.logout().then(), after the web-redirect. Used by
  // each call site to drop whichever loading flag(s) it owns
  // (Context.setLoadingOverlay vs a local setIsLoading) — kept as a
  // caller-supplied callback so this hook doesn't silently introduce a
  // setLoadingOverlay(false) for screens that didn't have one.
  //
  // Stored via ref so callers don't need to memoize — the interval is
  // not restarted when the callback identity changes.
  onLogout?: () => void;
};

// useIdleLogout polls AsyncStorage('lastActiveTime') every minute and logs
// the user out if the gap exceeds SESSION_EXPIRE_TIME. Extracted from a
// pattern copy-pasted across 9 screens with identical setInterval bodies.
//
// FriendsScreen is NOT migrated — it skips the web-redirect and sets a
// local isLogout state instead. Special enough that absorbing it would
// muddy the signature.
const useIdleLogout = ({enabled = true, onLogout}: Options = {}) => {
  const Context = useContext(StoreContext);
  const onLogoutRef = useRef(onLogout);

  useEffect(() => {
    onLogoutRef.current = onLogout;
  }, [onLogout]);

  useEffect(() => {
    if (!enabled) return;

    const handle = setInterval(() => {
      (async () => {
        const lastActiveTime = await AsyncStorage.getItem('lastActiveTime');

        if (lastActiveTime && moment().diff(moment(lastActiveTime), 'minutes') > SESSION_EXPIRE_TIME) {
          Context.logout().then(() => {
            if (Platform.OS === 'web') window.open(LANDING_PAGE_URL, '_self');
            onLogoutRef.current?.();
          });
        }
      })();
    }, 1000 * 60);

    return () => {
      clearInterval(handle);
    };
  }, [enabled]);
};

export default useIdleLogout;
